import { displayToast } from '../RTToast';
import {
    chargeSavedCardForReceipt,
    saveCardFromPayment,
    type ChargeSavedCardBody,
    type ChargeSavedCardResult
} from '../../api/customerCards/api';
import type { SavedCardPaymentState } from './SavedCardPicker';
import { readOverBalance, type OverBalanceInfo } from '../OverBalanceConfirm/overBalance';

/**
 * What a receipt dialog does with a saved-card charge outcome.
 *
 *   ISSUED  the receipt exists — show it
 *   RETRY   no money moved (a decline, or our own refusal) — keep the dialog open
 *   CLOSE   money may have moved or was taken — close the dialog, refresh, do NOT
 *           let it be resubmitted
 */
export type SavedCardChargeDisposition = 'ISSUED' | 'RETRY' | 'CLOSE';

export const savedCardChargeDisposition = (result: ChargeSavedCardResult): SavedCardChargeDisposition => {
    switch (result.status) {
        case 'SUCCESS':
            return 'ISSUED';
        case 'FAILED':
        case 'REFUSED':
        case 'UNRESOLVED_ATTEMPT':
            return 'RETRY';
        case 'DUPLICATE':
            return result.attempt?.attemptStatus === 'FAILED' ? 'RETRY' : 'CLOSE';
        case 'UNKNOWN':
        case 'CHARGED_NO_RECEIPT':
        default:
            return 'CLOSE';
    }
};

/**
 * Whether the dialog must mint a NEW idempotency key before the agent may try
 * again. Only when the old key is spent on an attempt that definitely moved no
 * money (a decline). Never while a charge is in flight, and never after an
 * outcome that may have moved money — the old key is what keeps a resubmit safe.
 */
export const shouldRemintChargeKey = (result: ChargeSavedCardResult): boolean =>
    result.status === 'FAILED' || (result.status === 'DUPLICATE' && result.attempt?.attemptStatus === 'FAILED');

/**
 * Submit-time check that the saved card belongs to the receipt's paying
 * passenger. Returns the message to show, or null when the charge may go. The
 * server enforces the same rule; this only saves a round trip and a confusing
 * refusal.
 */
export const savedCardPayerProblem = (
    savedCardPayment: SavedCardPaymentState | null | undefined,
    payerPassengerId: number | string | null | undefined
): string | null => {
    if (!savedCardPayment?.cardId) return null;
    if (payerPassengerId == null || payerPassengerId === '') {
        return 'A saved card can only pay for a passenger — choose the paying passenger first.';
    }
    if (savedCardPayment.cardPassengerId == null || String(savedCardPayment.cardPassengerId) !== String(payerPassengerId)) {
        return 'The selected saved card belongs to a different passenger than the payer. Choose the card again.';
    }
    return null;
};

const isStillProcessing = (result: ChargeSavedCardResult) =>
    result.status === 'DUPLICATE' && result.attempt?.attemptStatus === 'PENDING';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Charge, and when the server says this key's attempt is still PENDING (a
 * resubmit of a charge that is still running), keep waiting instead of treating
 * it as a refusal: re-ask with the SAME key — a known key never charges again, it
 * only reports — until the first attempt finishes or `maxWaitMs` passes.
 *
 * `onStillProcessing` fires once, so the dialog can say so while its loader stays.
 *
 * `confirmOverBalance`: when the server refuses because the amount is over the
 * travel file's balance, ask the agent; on yes, send the same charge again with
 * `allowOverBalance`. The refusal comes before the server records the attempt, so
 * the same idempotency key is still unused.
 */
export const runSavedCardCharge = async (
    body: ChargeSavedCardBody,
    options: {
        onStillProcessing?: () => void;
        confirmOverBalance?: (info: OverBalanceInfo) => Promise<boolean>;
        pollMs?: number;
        maxWaitMs?: number;
    } = {}
): Promise<ChargeSavedCardResult> => {
    const pollMs = options.pollMs ?? 3000;
    const maxWaitMs = options.maxWaitMs ?? 120_000;
    let result = await chargeSavedCardForReceipt(body);
    const overBalance = result.status === 'REFUSED' ? readOverBalance(result) : null;
    if (overBalance && options.confirmOverBalance) {
        if (!(await options.confirmOverBalance(overBalance))) {
            return { status: 'REFUSED', message: 'Not charged — the amount is over the travel file’s balance.' };
        }
        body = { ...body, allowOverBalance: true };
        result = await chargeSavedCardForReceipt(body);
    }
    const started = Date.now();
    let told = false;
    while (isStillProcessing(result) && Date.now() - started < maxWaitMs) {
        if (!told) {
            told = true;
            options.onStillProcessing?.();
        }
        await sleep(pollMs);
        result = await chargeSavedCardForReceipt(body);
    }
    return result;
};

/**
 * Tell the agent how a saved-card charge ended. Returns true only on SUCCESS.
 *
 * FAILED is Pelecard's own refusal — its code and message are shown, and trying
 * another card is safe. UNKNOWN and CHARGED_NO_RECEIPT mean money may have moved,
 * so the message says not to retry. DUPLICATE says what happened to the earlier
 * attempt with the same key.
 */
export const reportSavedCardCharge = (result: ChargeSavedCardResult): boolean => {
    switch (result.status) {
        case 'SUCCESS':
            displayToast('success', 'Card charged', 'The saved card was charged and the receipt issued.');
            return true;
        case 'FAILED':
            displayToast('error', 'Pelecard declined the charge', result.message);
            return false;
        case 'UNKNOWN':
            displayToast('error', 'Charge not confirmed — do NOT retry', result.message);
            return false;
        case 'CHARGED_NO_RECEIPT':
            displayToast('error', 'Card charged, receipt NOT created — do NOT retry', result.message);
            return false;
        case 'DUPLICATE': {
            const attemptStatus = result.attempt?.attemptStatus;
            if (attemptStatus === 'SUCCESS' && result.attempt?.documentId) {
                displayToast('info', 'Already charged', result.message);
            } else if (attemptStatus === 'FAILED') {
                displayToast('warning', 'This attempt was already declined', result.message);
            } else if (attemptStatus === 'PENDING') {
                displayToast('warning', 'Still processing — do NOT retry', result.message);
            } else {
                displayToast('error', 'Already submitted — do NOT retry', result.message);
            }
            return false;
        }
        case 'UNRESOLVED_ATTEMPT':
            displayToast('error', 'An earlier charge must be resolved first', result.message);
            return false;
        default:
            displayToast('error', 'Charge refused', result.message);
            return false;
    }
};

/**
 * After a receipt paid on the payment page has been issued: save the card for the
 * paying passenger. Always — there is no "save this card" choice (decided
 * 2026-09-16; the customer's consent rests on the agency's terms), so the route's
 * required `consent: true` is sent every time and the server records who saved it
 * and when.
 *
 * Only for a passenger payer: no passenger (a travel-agent payer, a typed-in
 * beneficiary, a supplier) means nothing to save and nothing to say. The server
 * still refuses a passenger who is not on the travel file, a travel file booked
 * through an agent, and a payment that did not become a receipt there.
 *
 * Best-effort — the receipt already exists. A failure only means the card was not
 * saved, which the agent is told in a warning; a card that was already saved is
 * silent.
 *
 * `passengerId` is the paying passenger as the form holds it AT SUBMIT TIME
 * (never a value synced into the form earlier). The browser sends only the
 * transaction reference; the server reads the token.
 */
export const saveCardAfterPagePayment = async (args: {
    passengerId?: number | string | null;
    /** paymentMethodObject.reference — Pelecard's transaction id on the receipt. */
    reference?: string | null;
    travelFileId?: number | string | null;
}): Promise<void> => {
    const { passengerId, reference, travelFileId } = args;
    if (passengerId == null || passengerId === '') return;
    if (!reference || !travelFileId) {
        displayToast(
            'warning',
            'Card not saved',
            'The receipt was issued, but the card could not be saved: the payment reference is missing.'
        );
        return;
    }
    try {
        const { alreadySaved } = await saveCardFromPayment({
            pelecardTransactionId: String(reference),
            passengerId,
            travelFileId,
            consent: true
        });
        if (!alreadySaved) displayToast('success', 'Card saved', 'The card was saved for the passenger.');
    } catch (err: any) {
        displayToast(
            'warning',
            'Card not saved',
            err?.response?.data?.message || 'The receipt was issued, but the card could not be saved.'
        );
    }
};
